import { Page } from '@testsigma/organism';
import { useCallback, useEffect, useState } from 'react';

const useFetchListData = <T>(
  apiService: (
    filterQuery?: string | Record<string, string>,
  ) => Promise<Page<T>>,
  params: Record<string, string>,
  dataProcessor?: (content: T[]) => Promise<T[]>,
  canConvertParamsAsString: boolean = true,
) => {
  const [data, setData] = useState<Page<T>>(new Page<T>());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const handleLoadMore = useCallback(
    (_page?: number, search?: string) => {
      setPage(page + 1);
    },
    [page],
  );

  const fetchData = useCallback(
    async (_page: number) => {
      setLoading(true);
      const query = Object.keys(params).length
        ? canConvertParamsAsString
          ? `?${Object.keys(params)
              .map((paramKey) => `${paramKey}=${params[paramKey]}`)
              .join('&')}&page=${_page}`
          : params['query']
        : params;
      if (!canConvertParamsAsString) params.page = `${_page}`;
      let response = await apiService(query);
      setLoading(false);
      if (Array.isArray(response) && !response.length) {
        response = new Page<T>();
      }
      const processedContent = dataProcessor
        ? await dataProcessor(response?.content || [])
        : response?.content || [];
      setData((oldData) => {
        if (oldData && oldData.content && _page > 0) {
          response.content = [...oldData.content, ...processedContent];
        } else {
          response.content = [...processedContent];
        }
        return response;
      });
      setHasMore(_page < response.totalPages - 1);
      setLoading(false);
    },
    [apiService, dataProcessor, params, page],
  );

  const refreshListData = useCallback(() => {
    fetchData(0);
    setPage(0);
  }, [fetchData, setPage]);

  useEffect(() => {
    if ((page != 0 && data?.content?.length) || !params) {
      fetchData(page);
    }
  }, [page]);

  useEffect(() => {
    fetchData(0);
    setPage(0);
  }, [params]);

  const refreshPage = () => {
    fetchData(page);
  };

  return {
    data,
    content: data.content,
    loading,
    hasMore,
    setData,
    handleLoadMore,
    refreshPage,
    refreshListData,
    page,
  };
};

export default useFetchListData;
